import { logger } from '@/utils/logger';

export interface SignalRToken {
  token: string;
  expiresIn: number; // seconds
  tokenType: string;
}

class SignalRAuthService {
  private token?: string;
  private tokenExpiry?: Date;
  private refreshTimer?: NodeJS.Timeout;
  private refreshPromise?: Promise<string>;

  /**
   * Gets a valid SignalR JWT token, refreshing if necessary
   */
  async getToken(): Promise<string> {
    // If we're already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check if we have a valid token
    if (this.token && this.tokenExpiry && this.tokenExpiry > new Date()) {
      return this.token;
    }

    // Token is missing or expired, refresh it
    this.refreshPromise = this.refreshToken();
    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = undefined;
    }
  }

  /**
   * Refreshes the SignalR JWT token
   */
  private async refreshToken(): Promise<string> {
    try {
      logger.info('Refreshing SignalR JWT token', 'SignalRAuthService');

      const response = await fetch('/api/signalr-auth/token', {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get SignalR token: ${response.status} ${response.statusText}`);
      }

      const data: SignalRToken = await response.json();
      
      // Store the token and calculate expiry
      this.token = data.token;
      // Set expiry to 30 seconds before actual expiry to ensure we refresh in time
      const expiryMs = (data.expiresIn - 30) * 1000;
      this.tokenExpiry = new Date(Date.now() + expiryMs);

      // Schedule next refresh
      this.scheduleRefresh(expiryMs);

      logger.info('SignalR JWT token refreshed successfully', 'SignalRAuthService', {
        expiresIn: data.expiresIn,
        scheduledRefreshIn: expiryMs / 1000
      });

      return data.token;
    } catch (_error) {
      logger.error('Failed to refresh SignalR token', 'SignalRAuthService', _error);
      // Clear token data on error
      this.clearToken();
      throw _error;
    }
  }

  /**
   * Schedules the next token refresh
   */
  private scheduleRefresh(delayMs: number): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Schedule the refresh
    this.refreshTimer = setTimeout(() => {
      logger.debug('Scheduled token refresh triggered', 'SignalRAuthService');
      this.getToken().catch(_error => {
        logger.error('Scheduled token refresh failed', 'SignalRAuthService', _error);
      });
    }, delayMs);
  }

  /**
   * Clears the stored token and cancels refresh timer
   */
  clearToken(): void {
    this.token = undefined;
    this.tokenExpiry = undefined;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    logger.info('SignalR token cleared', 'SignalRAuthService');
  }

  /**
   * Forces a token refresh on the next getToken call
   */
  forceRefresh(): void {
    logger.info('Forcing SignalR token refresh', 'SignalRAuthService');
    this.tokenExpiry = new Date(0); // Set expiry to past
  }
}

// Export singleton instance
export const signalRAuthService = new SignalRAuthService();